import {StyledPatientsWrapper, StyledSelectCell} from '@pages/patients/style.ts'
import {TableContactCell} from '@components/commons/table-contact-cell/TableContactCell.tsx'
import {Helmet} from 'react-helmet'
import i18n, {Trans, useTranslation} from '@/translations/i18n.tsx'
import {ButtonLink} from '@components/ui/button-link/ButtonLink.tsx'
import {AlertTriangleIcon, Download01Icon, PlusIcon, RefreshCw05Icon} from '@components/ui/icon/Icon.tsx'
import {PageHeading} from '@components/ui/page-atoms/PageAtoms.tsx'
import {
    debounce,
    filtersParamToPayload,
    getIsRowSelected,
    sortParamToPayload,
    workServiceToLabel,
    columnVisibilitySearchParamsToPayload,
    columnVisibilityToSearchParams
} from '@utilities/helpers.ts'
import {columnNameToLabel, usePatientsSearchParams} from '@/features/patients/utils'
import {ChangeEvent, useRef, useState} from 'react'
import {ErrorBox} from '@components/commons/error-box/ErrorBox.tsx'
import {Table} from '@components/commons/table/Table.tsx'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {Button} from '@components/ui/button/Button.tsx'
import {Link, useNavigate} from 'react-router-dom'
import {ColumnVisibilityMenu} from '@components/commons/columns-visibility-menu/ColumnVisibilityMenu.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {TableBodyCell, TableBodyCellText} from '@components/ui/table-atoms/TableAtoms.tsx'
import dayjs, {formatLocaleDate} from '@/dayjs.ts'
import {SelectColumnHeader} from '@components/ui/select-column-header/SelectColumnHeader.tsx'
import {SelectRowCheckbox} from '@components/ui/select-row-checkbox/SelectRowCheckbox.tsx'
import {usePatients} from '@/features/patients/services/usePatients.ts'
import {RowMenu} from '@/features/patients/components/row-menu/RowMenu.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {Tooltip} from '@components/ui/tooltip/Tooltip.tsx'
import {RowWarningTooltip} from '@components/commons/row-warning-tootlip/RowWarningTooltip.tsx'
import {FiltersBar} from '@components/ui/filters-bar/FiltersBar.tsx'
import {TableInputSearch} from '@components/commons/table-input-search/TableInputSearch.tsx'
import {TableBodyRow} from '@components/ui/table-body-row/TableBodyRow.tsx'
import {MoreActionsCell} from '@components/ui/more-actions-cell/MoreActionsCell.tsx'
import {SelectedRowsText} from '@components/commons/selected-rows-text/SelectedRowsText.tsx'
import {TableFilters} from '@components/commons/table-filters/TableFilters.tsx'
import {SortingMenu} from '@/components/commons/sorting-menu/SortingMenu'
import {PatientsColumn} from '@/features/patients/types'
import {ViewSelect} from '@/features/views/views-select/ViewSelect'

import {StatusBullet} from '@/features/projects/components/status-bullet/StatusBullet'
import {projectStatusToLabel} from '@/features/projects/utils'
import {StyledStatusCell} from '../users/style'
import {usePermissions} from '@/features/auth/store/store'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'
import {httpExportPatient} from '@/features/patients/services/patients.http'

const makeColumnsMap = () =>
    [
        {
            id: PatientsColumn.enum.fullName,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.fullName])
        },
        {
            id: PatientsColumn.enum.birthDate,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.birthDate])
        },
        {
            id: PatientsColumn.enum.serviceType,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.serviceType])
        },
        {
            id: PatientsColumn.enum.outRegion,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.outRegion])
        },
        {
            id: PatientsColumn.enum.residenceAslDistrictId,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.residenceAslDistrictId])
        },
        {
            id: PatientsColumn.enum.residenceCityId,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.residenceCityId])
        },
        {
            id: PatientsColumn.enum.residenceProvinceId,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.residenceProvinceId])
        },
        {
            id: PatientsColumn.enum.neuromuscularDiagnosisId,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.neuromuscularDiagnosisId])
        },
        {
            id: PatientsColumn.enum.mobilePhone,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.mobilePhone])
        },
        {
            id: PatientsColumn.enum.telephone,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.telephone])
        },
        {
            id: PatientsColumn.enum.email,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.email])
        },
        {
            id: PatientsColumn.enum.projectStatus,
            label: i18n.t(columnNameToLabel[PatientsColumn.enum.projectStatus])
        }
    ] as const satisfies {id: PatientsColumn; label: string}[]

export const Patients = () => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const permissions = usePermissions()
    const columnsMap = makeColumnsMap()
    const [isSelectAll, setIsSelectAll] = useState(false)
    const [unselectedPatientsIds, setUnselectedPatientsIds] = useState<string[]>([])
    const [selectedPatientsIds, setSelectedPatientsIds] = useState<string[]>([])
    const {searchParams, setSearchParams} = usePatientsSearchParams()
    const inputSearchRef = useRef<HTMLInputElement | null>(null)
    const patientsQuery = usePatients(
        {
            limit: searchParams.limit,
            page: searchParams.page,
            search: searchParams.search
        },
        {
            filters: filtersParamToPayload(searchParams.filters)?.filters,
            sorts: sortParamToPayload(searchParams.sorts)
        }
    )
    const selectedPatientsCount = isSelectAll
        ? patientsQuery.persistedPagination.total - unselectedPatientsIds.length
        : selectedPatientsIds.length

    const resetSelectionStates = () => {
        setIsSelectAll(false)
        setSelectedPatientsIds([])
        setUnselectedPatientsIds([])
    }

    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined, page: 1})
    }, 1000)

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(PatientsColumn, searchParams.columnVisibility) ??
        Object.fromEntries(PatientsColumn.options.map(column => [column, column != PatientsColumn.enum.projectStatus]))

    return (
        <StyledPatientsWrapper>
            <Helmet title={t('patients:meta_title')} />
            <PageHeading justify="space-between">
                <h2>{t('commons:patients')}</h2>
                {permissions?.patients?.write && (
                    <ButtonLink variant="primaryNeutral" to={routes.CREATE_PATIENT.path}>
                        <PlusIcon />
                        {t('commons:patient')}
                    </ButtonLink>
                )}
            </PageHeading>
            <FiltersBar>
                <Flexbox align="center" gap={2}>
                    <TableInputSearch
                        ref={inputSearchRef}
                        defaultValue={searchParams.search}
                        onChange={onSearch}
                        placeholder={t('commons:search_name')}
                    />
                    {selectedPatientsCount >= 1 && (
                        <>
                            <SelectedRowsText count={selectedPatientsCount} />
                            <ExportModal
                                entity="patients"
                                trigger={
                                    <Button variant="ghost" size="sm">
                                        <Download01Icon />
                                        {t('commons:export')}
                                    </Button>
                                }
                                fileName="Pazienti"
                                promise={({extension, mode, customColumns}) =>
                                    httpExportPatient({
                                        payload: {
                                            selectedPatientIds: selectedPatientsIds,
                                            exceptedPatientIds: unselectedPatientsIds,
                                            type: extension,
                                            filters: filtersParamToPayload(searchParams.filters)?.filters,
                                            fields:
                                                mode == 'custom'
                                                    ? customColumns
                                                    : mode == 'view'
                                                      ? PatientsColumn.exclude([
                                                            'select',
                                                            'moreActions'
                                                        ]).options.filter(column => columnVisibility[column])
                                                      : PatientsColumn.exclude(['select', 'moreActions']).options,
                                            sorts: sortParamToPayload(searchParams.sorts)
                                        }
                                    })
                                }
                                selectedCount={selectedPatientsCount}
                                allowedExtensions={['xlsx', 'xls', 'csv']}
                            />
                        </>
                    )}
                </Flexbox>
                {selectedPatientsCount == 0 && <ViewSelect entity="patients" searchInputRef={inputSearchRef} />}
                {selectedPatientsCount == 0 && (
                    <Flexbox style={{flexShrink: 0}} align="center" gap={2}>
                        <TableFilters
                            onApply={filters => {
                                setSearchParams({filters: JSON.stringify(filters), page: 1})
                                resetSelectionStates()
                            }}
                            filtersParams={searchParams.filters}
                            entity="patients"
                            availableFilters={[
                                {
                                    key: PatientsColumn.enum.birthDate,
                                    label: columnNameToLabel[PatientsColumn.enum.birthDate]
                                },
                                {
                                    key: PatientsColumn.enum.serviceType,
                                    label: columnNameToLabel[PatientsColumn.enum.serviceType]
                                },
                                {
                                    key: PatientsColumn.enum.outRegion,
                                    label: columnNameToLabel[PatientsColumn.enum.outRegion]
                                },
                                {
                                    key: PatientsColumn.enum.residenceAslDistrictId,
                                    label: columnNameToLabel[PatientsColumn.enum.residenceAslDistrictId]
                                },
                                {
                                    key: PatientsColumn.enum.residenceCityId,
                                    label: columnNameToLabel[PatientsColumn.enum.residenceCityId]
                                },
                                {
                                    key: PatientsColumn.enum.residenceProvinceId,
                                    label: columnNameToLabel[PatientsColumn.enum.residenceProvinceId]
                                },
                                {
                                    key: PatientsColumn.enum.neuromuscularDiagnosisId,
                                    label: columnNameToLabel[PatientsColumn.enum.neuromuscularDiagnosisId]
                                },
                                {
                                    key: PatientsColumn.enum.mobilePhone,
                                    label: columnNameToLabel[PatientsColumn.enum.mobilePhone]
                                },
                                {
                                    key: PatientsColumn.enum.telephone,
                                    label: columnNameToLabel[PatientsColumn.enum.telephone]
                                },
                                {
                                    key: PatientsColumn.enum.email,
                                    label: columnNameToLabel[PatientsColumn.enum.email]
                                },
                                {
                                    key: PatientsColumn.enum.projectStatus,
                                    label: columnNameToLabel[PatientsColumn.enum.projectStatus]
                                }
                            ]}
                        />
                        <SortingMenu
                            columns={columnsMap}
                            order={sortParamToPayload(searchParams.sorts) ?? []}
                            setOrder={sorts => {
                                setSearchParams({sorts: JSON.stringify(sorts)})
                                resetSelectionStates()
                            }}
                        />
                        <ColumnVisibilityMenu
                            columns={columnsMap}
                            columnVisibility={columnVisibility}
                            onSubmit={columnVisibility =>
                                setSearchParams({columnVisibility: columnVisibilityToSearchParams(columnVisibility)})
                            }
                        />
                    </Flexbox>
                )}
            </FiltersBar>
            {patientsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('patients:no_patients_title')}
                            subtitle={t('patients:no_patients_description')}
                        >
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setSearchParams({search: undefined, filters: undefined, view: undefined, page: 1})
                                    if (inputSearchRef.current) {
                                        inputSearchRef.current.value = ''
                                    }
                                }}
                            >
                                {t('patients:no_patients_cta')}
                            </Button>
                        </EmptyState>
                    }
                    rowComponent={({tableBodyRowProps, dataItem, index}) => (
                        <TableBodyRow
                            onClick={dataItem ? () => navigate(dataItem.id.toString()) : undefined}
                            key={index}
                            {...tableBodyRowProps}
                        />
                    )}
                    pagination={{
                        ...patientsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => {
                            resetSelectionStates()
                            setSearchParams({limit, page: 1})
                        }
                    }}
                    columnPinning={{
                        left: [PatientsColumn.enum.select, PatientsColumn.enum.fullName],
                        right: [PatientsColumn.enum.moreActions]
                    }}
                    columnVisibility={columnVisibility}
                    getIsRowSelected={({dataItem}) =>
                        getIsRowSelected({
                            dataItem,
                            selectedIds: selectedPatientsIds,
                            unselectedIds: unselectedPatientsIds,
                            isSelectAll
                        })
                    }
                    isLoading={patientsQuery.isPending}
                    data={patientsQuery.data?.data ?? []}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledSelectCell>
                                        {dataItem.fieldsToCompile.length >= 1 && (
                                            <Tooltip
                                                contentProps={{onClick: event => event.stopPropagation()}}
                                                maxWidth="320px"
                                                content={
                                                    <RowWarningTooltip
                                                        icon={RefreshCw05Icon}
                                                        title={t('patients:missing_data_title')}
                                                        paragraph={
                                                            <Trans
                                                                i18nKey="patients:missing_data_paragraph"
                                                                components={{
                                                                    cta: <Link to={dataItem.id.toString()} />
                                                                }}
                                                            />
                                                        }
                                                    />
                                                }
                                                triggerProps={{asChild: true}}
                                                variant="light"
                                            >
                                                <RefreshCw05Icon size={16} />
                                            </Tooltip>
                                        )}
                                        {permissions?.patients.write && (
                                            <SelectRowCheckbox
                                                isSelectAll={isSelectAll}
                                                setSelectedIds={setSelectedPatientsIds}
                                                getIsRowSelected={({dataItem}) =>
                                                    getIsRowSelected({
                                                        dataItem,
                                                        selectedIds: selectedPatientsIds,
                                                        unselectedIds: unselectedPatientsIds,
                                                        isSelectAll
                                                    })
                                                }
                                                dataItem={dataItem}
                                                setUnselectedIds={setUnselectedPatientsIds}
                                            />
                                        )}
                                    </StyledSelectCell>
                                </TableBodyCell>
                            ),
                            header: () =>
                                permissions?.patients.write ? (
                                    <SelectColumnHeader
                                        currentPageIds={patientsQuery.data?.data.map(patient => patient.id) ?? []}
                                        isSelectAll={isSelectAll}
                                        selectedIds={selectedPatientsIds}
                                        setIsSelectAll={setIsSelectAll}
                                        unselectedIds={unselectedPatientsIds}
                                        setUnselectedIds={setUnselectedPatientsIds}
                                        setSelectedIds={setSelectedPatientsIds}
                                        selectedCount={selectedPatientsCount}
                                        paginationTotal={patientsQuery.persistedPagination.total}
                                    />
                                ) : (
                                    <></>
                                ),
                            id: PatientsColumn.enum.select,
                            size: () => 64
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText variant="dark">{dataItem.fullName}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.fullName]),
                            id: PatientsColumn.enum.fullName,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                const years = dayjs().diff(dataItem.birthDate, 'years')
                                const months = dayjs().diff(dataItem.birthDate, 'months')
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {formatLocaleDate(dataItem.birthDate, 'll')}
                                            {` `}(
                                            {years > 0
                                                ? t('commons:years_age', {count: years})
                                                : months > 0
                                                  ? t('commons:months_age', {count: months})
                                                  : ''}
                                            )
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[PatientsColumn.enum.birthDate]),
                            id: PatientsColumn.enum.birthDate,
                            size: () => 200
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{t(workServiceToLabel[dataItem.serviceType])}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.serviceType]),
                            id: PatientsColumn.enum.serviceType,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.residenceAslDistrict ? t('commons:no') : t('commons:yes')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.outRegion]),
                            id: PatientsColumn.enum.outRegion,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.residenceAslDistrict
                                            ? `${dataItem.residenceAslDistrict.aslName} - ${dataItem.residenceAslDistrict.name}`
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.residenceAslDistrictId]),
                            id: PatientsColumn.enum.residenceAslDistrictId,
                            minSize: () => 80,
                            size: () => 240,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.residence?.city.name ?? '-'}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.residenceCityId]),
                            id: PatientsColumn.enum.residenceCityId,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.residence?.city.provinceAcronym ?? '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.residenceProvinceId]),
                            id: PatientsColumn.enum.residenceProvinceId,
                            size: () => 160
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.neuromuscularDiagnosis?.name ?? '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.neuromuscularDiagnosisId]),
                            id: PatientsColumn.enum.neuromuscularDiagnosisId,
                            size: () => 240,
                            maxSize: () => 600
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableContactCell
                                    contacts={dataItem.contacts.filter(contact => contact.type == 'email')}
                                    forwardedProps={forwardedProps}
                                />
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.email]),
                            id: PatientsColumn.enum.email,
                            size: () => 160,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableContactCell
                                    contacts={dataItem.contacts.filter(contact => contact.type == 'telephone')}
                                    forwardedProps={forwardedProps}
                                />
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.telephone]),
                            id: PatientsColumn.enum.telephone,
                            size: () => 160,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableContactCell
                                    contacts={dataItem.contacts.filter(contact => contact.type == 'mobile_phone')}
                                    forwardedProps={forwardedProps}
                                />
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.mobilePhone]),
                            id: PatientsColumn.enum.mobilePhone,
                            size: () => 160,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <MoreActionsCell {...forwardedProps}>
                                    <RowMenu
                                        patient={dataItem}
                                        onDeleteSuccess={() => {
                                            setSelectedPatientsIds(prev => prev.filter(id => id != dataItem.id))
                                            setUnselectedPatientsIds(prev => prev.filter(id => id != dataItem.id))
                                        }}
                                    />
                                </MoreActionsCell>
                            ),
                            header: () => undefined,
                            id: PatientsColumn.enum.moreActions,
                            size: () => 56
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledStatusCell>
                                        {dataItem.project?.status ? (
                                            <>
                                                <StatusBullet status={dataItem.project.status} />
                                                {t(projectStatusToLabel[dataItem.project.status])}
                                            </>
                                        ) : (
                                            t('commons:none')
                                        )}
                                    </StyledStatusCell>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientsColumn.enum.projectStatus]),
                            id: PatientsColumn.enum.projectStatus,
                            size: () => 140
                        }
                    ]}
                />
            )}
        </StyledPatientsWrapper>
    )
}
